import { Alert } from '@mui/material';
import { useMemo } from 'react';
import { useOutletContext } from 'react-router';
import Tabs from '../../../../components/Tabs';
import { useLanguage } from '../../../../contexts/language';
import { ContractTemplateSubPageContext } from '../types';
import InsuranceTabContent from './InsuranceTabContent';
import useDraftList, { ListElement } from '../../../../hooks/useDraftList';
import { ContractInsurance } from '../../../../types/insurance';
import useFreshDataEnsurer from '../../../../hooks/useFreshDataEnsurer';
import { useNavigationGuardBlocker } from '../../../../hooks/useNavigationGuard';
import { useCurrentUser } from '../../../../contexts/currentUser';

const ContractInsurancesPage = () => {
  const { contract, insuranceManagement, isContractDisabled, refetchContract } =
    useOutletContext<ContractTemplateSubPageContext>();
  const { isOperator } = useCurrentUser();
  const {
    insurances,
    refetch: refecthInsurance,
    isFetching: isFetchingInsurances,
  } = insuranceManagement;
  const { t } = useLanguage();

  const areInsurancesFresh = useFreshDataEnsurer(
    isFetchingInsurances,
    refecthInsurance,
  );

  const { list, isReady, operations } = useDraftList(
    insurances,
    areInsurancesFresh,
  );

  const { nonExpiredInsurances, expiredInsurances } = useMemo(() => {
    const nonExpiredInsurances: ListElement<ContractInsurance>[] = [];
    const expiredInsurances: ListElement<ContractInsurance>[] = [];

    list.forEach(item => {
      if (item.isDraft || !item.data.isExpired) {
        nonExpiredInsurances.push(item);
      } else {
        expiredInsurances.push(item);
      }
    });

    return {
      nonExpiredInsurances,
      expiredInsurances,
    };
  }, [list]);

  useNavigationGuardBlocker(() => Boolean(list.find(item => item.isDraft)));

  const canInteractWithInsurances = !isOperator && !isContractDisabled;

  return (
    <Tabs
      tabs={[{ label: t('content.valid') }, { label: t('content.expired') }]}
      variant="buttons"
    >
      <InsuranceTabContent
        contract={contract}
        draftListManager={{ list: nonExpiredInsurances, isReady, operations }}
        areInsurancesPending={!areInsurancesFresh}
        isFetchingInsurances={isFetchingInsurances}
        onInsurancesDataUpdate={() => {
          refetchContract();
          refecthInsurance();
        }}
        emptyContent={
          canInteractWithInsurances ? (
            <Alert severity="error" className="mb-4 shadow-iots">
              {t('validation.valid-insurances-minimum-button')}
            </Alert>
          ) : (
            <div className="grid place-items-center w-full grow">
              <h1 className="text-2xl font-bold">
                {t(
                  isOperator
                    ? 'content.contact-admin-to-add-insurance'
                    : 'content.no-insurances-found',
                )}
              </h1>
            </div>
          )
        }
        showAddButton={canInteractWithInsurances}
        canInteractWithInsurances={canInteractWithInsurances}
      />
      <InsuranceTabContent
        contract={contract}
        draftListManager={{ list: expiredInsurances, isReady, operations }}
        areInsurancesPending={!areInsurancesFresh}
        isFetchingInsurances={isFetchingInsurances}
        onInsurancesDataUpdate={() => {
          refetchContract();
          refecthInsurance();
        }}
        emptyContent={
          <div className="grid place-items-center w-full grow">
            <h1 className="text-2xl font-bold">
              {t(
                isOperator
                  ? 'content.contact-admin-to-add-insurance'
                  : 'content.no-insurances-found',
              )}
            </h1>
          </div>
        }
        canInteractWithInsurances={canInteractWithInsurances}
      />
    </Tabs>
  );
};

export default ContractInsurancesPage;
